import { Component, DestroyRef, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { HttpParams } from '@angular/common/http'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { escapeHtml } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { CellEditEndingEventArgs, CellRangeEventArgs, CellType, FlexGrid, GridPanel } from '@grapecity/wijmo.grid'

import {
    BaseComponent,
    FeatureType,
    Grid,
    ModalSize,
    QuarterlyMeasurable,
    QuarterlyMeasurableDashboard,
    QuarterlyMeasurableDashboardRow,
    SaveQuarterlyMeasurableValue,
    formatQuarterlyMeasurableExact,
    formatQuarterlyMeasurableValue,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { QuarterlyMeasurableComponent } from './quarterly-measurable.component'

@Component({
    templateUrl: './quarterly-measurables.component.html',
    styleUrls: ['./quarterly-measurables.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        AutoSizeDirective,
        WjGridModule,
        WjGridFilterModule,
    ],
})
export class QuarterlyMeasurablesComponent extends BaseComponent {
    faSearch = faSearch
    public grid: Grid<QuarterlyMeasurable>
    public resultsGrid: Grid<QuarterlyMeasurableDashboardRow>

    dashboard: QuarterlyMeasurableDashboard | null = null
    quarterCount = 12

    quarterCountOptions = [
        { value: 12, label: '12 quarters (3 years)' },
        { value: 20, label: '20 quarters (5 years)' },
        { value: 40, label: '40 quarters (10 years)' },
    ]

    private destroyRef = inject(DestroyRef)
    private lastEditBegin: { id: string; key: string; value: number | null } | null = null

    constructor() {
        super()
        this.grid = new Grid<QuarterlyMeasurable>(this.services)
            .setupModalEditing(
                this.services.data.quarterlyMeasurables,
                'Quarterly Measurable',
                QuarterlyMeasurableComponent,
                () => new QuarterlyMeasurable(),
                this,
                ModalSize.medium,
            )
            .filterBy(item => [item.name, item.goalLabel, item.hrGoalName, item.calculatedSourceName])
        this.resultsGrid = new Grid<QuarterlyMeasurableDashboardRow>(this.services)
            .isReadOnly(!this.canEdit)
            .editOnDoubleClick(false)
            .doInitialSelection(false)
        this.grid.itemAdded.on(() => this.refresh())
        this.grid.itemEdited.on(() => this.refresh())
        this.refresh()
    }

    public refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data.load(true, true, this.services.data.quarterlyMeasurables.queryAll()).subscribe({
                next: result => {
                    this.initialLoadingComplete()
                    this.grid.items = result[0]
                    this.loadDashboard()
                },
            }),
        )
    }

    loadDashboard() {
        const params = new HttpParams().set('quarterCount', String(this.quarterCount))
        this.cancelableRequest(
            'dashboard',
            true,
            this.services.data.get<QuarterlyMeasurableDashboard>('/api/QuarterlyMeasurable/GetDashboard', params).subscribe({
                next: dashboard => {
                    dashboard.measurables.forEach(row => {
                        row.drafts = {}
                        dashboard.quarters.forEach(q => {
                            row.drafts[this.getDraftProperty(q.year, q.quarter)] =
                                row.sources[q.key] === 'manual' ? (row.values[q.key] ?? null) : null
                        })
                    })
                    this.dashboard = dashboard
                    this.resultsGrid.items = dashboard.measurables
                },
            }),
        )
    }

    onQuarterCountChanged() {
        this.loadDashboard()
    }

    formatValue(format: string | null | undefined, value: number | null | undefined, empty = ''): string {
        return formatQuarterlyMeasurableValue(format, value, empty)
    }

    itemFormatter = (panel: GridPanel, r: number, c: number, cell: HTMLDivElement) => {
        if (panel.cellType !== CellType.Cell) {
            return
        }
        const col = panel.grid.columns[c]
        if (col.binding !== 'goalValue') {
            return
        }
        const item = panel.rows[r].dataItem as QuarterlyMeasurable
        if (item?.goalValue == null) {
            return
        }
        cell.textContent = this.formatValue(item.format, item.goalValue)
        cell.title = formatQuarterlyMeasurableExact(item.goalValue)
    }

    resultsItemFormatter = (panel: GridPanel, r: number, c: number, cell: HTMLDivElement) => {
        const col = panel.grid.columns[c]
        const binding = col.binding
        const parsed = this.parseDraftBinding(binding)

        if (panel.cellType !== CellType.Cell) {
            return
        }

        const item = panel.rows[r].dataItem as QuarterlyMeasurableDashboardRow
        if (!item) {
            return
        }

        cell.classList.remove('metric-override', 'metric-placeholder')
        cell.removeAttribute('title')

        if (binding === 'name' && item.isCalculated) {
            cell.innerHTML = `${escapeHtml(item.name ?? '')} <span class="badge bg-secondary ms-1">Calculated</span>`
        } else if (binding === 'name' && item.isMetricLinked) {
            cell.innerHTML = `${escapeHtml(item.name ?? '')} <span class="badge bg-secondary ms-1">Metric</span>`
        }

        if (!parsed) {
            return
        }

        const isEditing =
            !!panel.grid.activeEditor && panel.grid.selection.row === r && panel.grid.selection.col === c
        if (isEditing) {
            return
        }

        const draft = this.normalizeDraft(item.drafts?.[parsed.draftProperty])
        if (draft != null) {
            cell.textContent = this.formatValue(item.format, draft)
            cell.title = formatQuarterlyMeasurableExact(draft)
            cell.classList.add('metric-override')
            return
        }

        const metric = item.metricValues?.[parsed.key]
        if (metric != null) {
            cell.textContent = this.formatValue(item.format, metric)
            cell.title = formatQuarterlyMeasurableExact(metric)
            cell.classList.add('metric-placeholder')
            return
        }

        cell.textContent = '—'
    }

    beginningEdit(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (!this.canEdit) {
            args.cancel = true
            return
        }

        const item = flexGrid.rows[args.row].dataItem as QuarterlyMeasurableDashboardRow
        const parsed = this.parseDraftBinding(flexGrid.columns[args.col].binding)
        if (!item || !parsed) {
            args.cancel = true
            return
        }

        this.lastEditBegin = {
            id: item.id,
            key: parsed.key,
            value: this.normalizeDraft(item.drafts[parsed.draftProperty]),
        }
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) {
            this.lastEditBegin = null
            return
        }

        const item = flexGrid.rows[args.row].dataItem as QuarterlyMeasurableDashboardRow
        const parsed = this.parseDraftBinding(flexGrid.columns[args.col].binding)
        if (!item || !parsed) {
            this.lastEditBegin = null
            return
        }

        const value = this.normalizeDraft(item.drafts[parsed.draftProperty])
        item.drafts[parsed.draftProperty] = value

        const unchanged =
            this.lastEditBegin && this.lastEditBegin.id === item.id && this.lastEditBegin.key === parsed.key
                ? Object.is(value, this.lastEditBegin.value)
                : false
        this.lastEditBegin = null
        if (unchanged) {
            return
        }

        const payload: SaveQuarterlyMeasurableValue[] = [
            {
                measurableId: item.id,
                year: parsed.year,
                quarter: parsed.quarter,
                value,
            },
        ]

        this.services.data
            .put('/api/QuarterlyMeasurable/SaveValues', payload)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => this.applySavedValue(item, parsed.key, value),
                error: () => this.services.notification.error('Failed to save quarterly measurable values'),
            })
    }

    pasting(_flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) {
            return
        }
        args.cancel = true
        this.services.notification.info('Pasting is not yet supported.')
    }

    public get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.UsersEdit])
    }

    private applySavedValue(item: QuarterlyMeasurableDashboardRow, key: string, value: number | null) {
        if (value == null) {
            const metric = item.metricValues?.[key] ?? null
            item.values[key] = metric
            item.sources[key] = metric != null ? 'metric' : ''
            return
        }

        item.values[key] = value
        item.sources[key] = 'manual'
    }

    draftBinding(year: number, quarter: number) {
        return `drafts.${this.getDraftProperty(year, quarter)}`
    }

    private getDraftProperty(year: number, quarter: number) {
        return `q${year}_${quarter}`
    }

    private parseDraftBinding(binding: string | null | undefined) {
        const match = /^drafts\.q(\d+)_(\d+)$/.exec(binding ?? '')
        if (!match) {
            return null
        }
        const year = Number(match[1])
        const quarter = Number(match[2])
        return {
            year,
            quarter,
            key: `${year}-${quarter}`,
            draftProperty: this.getDraftProperty(year, quarter),
        }
    }

    private normalizeDraft(value: unknown): number | null {
        if (value == null || value === '') {
            return null
        }
        const numeric = typeof value === 'number' ? value : Number(value)
        return Number.isNaN(numeric) ? null : numeric
    }
}
